import { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../lib/api";

export default function EnrollLogin() {
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/enroll/login`, form);
      localStorage.setItem("dh_candidate_token", res.data.access_token);
      localStorage.setItem("dh_candidate_email", res.data.email);
      toast.success("Welcome back!");
      nav("/enroll/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#FAF7EE] min-h-screen" data-testid="enroll-login-page">
      <div className="bg-[#FAF7EE] border-b border-[#0F2A1F]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/enroll" className="flex items-center gap-3" data-testid="login-logo">
            <div className="h-10 w-10 rounded-md bg-[#0F2A1F] text-white flex items-center justify-center font-bold text-sm">DH</div>
            <div className="leading-tight">
              <div className="font-serif text-lg text-[#0F2A1F]">DirectHire</div>
              <div className="text-[10px] tracking-[0.25em] text-[#0F2A1F]/55 uppercase">Enrollment</div>
            </div>
          </Link>
          <nav className="flex items-center gap-7 text-sm text-[#0F2A1F]/80">
            <Link to="/enroll" className="rounded-md bg-[#0F2A1F] text-white px-4 py-2 text-sm font-medium hover:bg-[#163b2c]" data-testid="login-apply-now">Apply now</Link>
          </nav>
        </div>
      </div>

      <section className="py-16 md:py-24">
        <div className="max-w-md mx-auto px-6">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">Sign in</div>
          <h1 className="mt-3 text-4xl md:text-5xl font-bold text-[#0F2A1F] leading-tight">Welcome back</h1>
          <p className="mt-3 text-[#0F2A1F]/65">Pick up where you left off in your 14-day journey.</p>

          <form onSubmit={submit} className="mt-10 rounded-xl bg-white p-8 ring-1 ring-[#0F2A1F]/10 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="lemail" className="text-[#0F2A1F]">Email</Label>
              <Input id="lemail" type="email" data-testid="login-email" className="mt-2 bg-[#FAF7EE] border-[#0F2A1F]/15 focus-visible:ring-[#0F2A1F]" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="lpw" className="text-[#0F2A1F]">Password</Label>
              <div className="mt-2 relative">
                <Input id="lpw" type={showPw ? "text" : "password"} data-testid="login-password" className="bg-[#FAF7EE] border-[#0F2A1F]/15 focus-visible:ring-[#0F2A1F] pr-10" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0F2A1F]/50 hover:text-[#0F2A1F]" data-testid="login-password-toggle" aria-label="Toggle password visibility">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={busy} className="w-full rounded-md bg-[#0F2A1F] text-white py-3.5 text-sm font-medium hover:bg-[#163b2c] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="login-submit-btn">
              {busy ? "Signing in…" : (<>Sign in <ArrowRight className="h-4 w-4" /></>)}
            </button>
            <div className="text-right">
              <Link to="/enroll/forgot-password" className="text-xs text-[#0F2A1F] hover:underline" data-testid="login-forgot-link">Forgot your password?</Link>
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-[#0F2A1F]/65">
            Don't have an account yet? <Link to="/enroll" className="text-[#0F2A1F] font-semibold hover:underline" data-testid="login-cta-apply">Apply now</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
